import { z } from 'zod';

import { BooleanParamSchema } from '../common/boolean-param/boolean-param';
import { UuidSchema } from '../common/id/id';
import { makeListQuery } from '../common/list-query/list-query';

import { AiCacheStorageSchema } from './ai-cache-storage/ai-cache-storage';
import { AiCapabilitySchema } from './ai-capability/ai-capability';
import { AiFeatureKeySchema } from './ai-feature-key/ai-feature-key';

/**
 * One cached AI response, as the admin cache browser sees it — **metadata only**.
 *
 * There is no `value` and no `storageKey`, and the shape cannot express either.
 * A cached response is whatever a member wrote into a chat message plus whatever
 * the model concluded about it; the operator surface `03-ai-platform.md` asks
 * for is "key hash, feature, hits, size, expiry" so staff can reason about hit
 * rates and purge a poisoned entry — not read the corpus. The storage key is
 * `hidden` at the repository too (N2, N12): a column that is never selected
 * cannot be leaked by a later mapper.
 *
 * The key hash travels because it is what makes an entry identifiable and
 * purgeable, and it reveals nothing — it is a digest of a request nobody can
 * invert.
 */
export const AiCacheEntrySchema = z
  .object({
    id: UuidSchema,
    keyHash: z.string(),
    featureKey: AiFeatureKeySchema,
    capability: AiCapabilitySchema,
    provider: z.string(),
    model: z.string(),
    storage: AiCacheStorageSchema,
    sizeBytes: z.number().int().min(0),
    hitCount: z.number().int().min(0),
    /** `null` means the entry never expires — the `ai.cache.ttlSeconds` null case. */
    expiresAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type AiCacheEntry = z.infer<typeof AiCacheEntrySchema>;

/** The whitelisted filters of the admin cache list (N7). */
export const AiCacheEntriesListQuerySchema = makeListQuery({
  featureKey: AiFeatureKeySchema.optional(),
  capability: AiCapabilitySchema.optional(),
  provider: z.string().min(1).max(64).optional(),
  model: z.string().min(1).max(128).optional(),
  storage: AiCacheStorageSchema.optional(),
  /** `true` lists only entries whose TTL has elapsed — the purge preview. */
  expired: BooleanParamSchema.optional(),
});

export type AiCacheEntriesListQuery = z.infer<typeof AiCacheEntriesListQuerySchema>;

/**
 * What a bulk purge targets.
 *
 * `expired` is the routine sweep the worker also runs. `all` is the blunt
 * instrument for a poisoned corpus after a prompt or model change — it is
 * separate, and named, because "purge everything" should never be reachable by
 * omitting a parameter.
 */
export const AI_CACHE_PURGE_SCOPES = ['expired', 'all'] as const;

export const AiCachePurgeScopeSchema = z.enum(AI_CACHE_PURGE_SCOPES);

export type AiCachePurgeScope = (typeof AI_CACHE_PURGE_SCOPES)[number];

export const PurgeAiCacheSchema = z
  .object({
    scope: AiCachePurgeScopeSchema,
    /** Narrows the purge to one feature — retuning moderation should not evict face-match. */
    featureKey: AiFeatureKeySchema.optional(),
  })
  .strict();

export type PurgeAiCacheRequest = z.infer<typeof PurgeAiCacheSchema>;

export const AiCachePurgeResultSchema = z
  .object({
    purged: z.number().int().min(0),
  })
  .strict();

export type AiCachePurgeResult = z.infer<typeof AiCachePurgeResultSchema>;
